import { z } from 'zod';
import { positiveDecimal, rate } from './primitives.js';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v === '' ? null : v));

/** Logo de l'entreprise : 500 Ko au plus, PNG ou JPEG. La signature du fichier est contrôlée côté API. */
export const LOGO_MAX_BYTES = 500_000;

export const companyUpdateSchema = z.object({
  version: z.number().int().positive(),
  name: z.string().trim().min(2).max(160).optional(),
  legalId: optionalText(120),
  address: optionalText(300),
  phone: optionalText(40),
  email: optionalText(160),
  logo: z.string().max(Math.ceil((LOGO_MAX_BYTES * 4) / 3) + 40).nullable().optional(),
  /** Pays et devise par défaut : valent pour les nouveaux projets ; aucun projet existant ni aucun prix n'est converti. */
  countryCode: z.string().length(2).toUpperCase().optional(),
  defaultCurrency: z.string().length(3).toUpperCase().optional(),
  settings: z.lazy(() => companySettingsSchema).optional(),
});

const docText = (max: number) => z.string().trim().max(max).nullable().optional();

/** Mentions reprises sur les documents et réglages par défaut des nouveaux projets. */
export const companySettingsSchema = z.object({
  documents: z
    .object({
      /** Lieu d'émission : « Fait à … ». À défaut, la localisation du projet. */
      city: docText(120),
      signatoryName: docText(120),
      signatoryTitle: docText(120),
      /** Coordonnées de paiement : banque, compte, paiement mobile. */
      paymentDetails: docText(800),
      quotationTerms: docText(1500),
      invoiceTerms: docText(1500),
      /** Mention légale de bas de document. */
      legalNotice: docText(400),
    })
    .optional(),
  /** TVA proposée aux nouveaux projets ; les réglages des projets existants ne changent pas. */
  defaults: z.object({ vatEnabled: z.boolean().optional(), vatRate: z.string().regex(/^0(\.\d{1,4})?$/).nullable().optional() }).optional(),
});
export type CompanySettings = z.infer<typeof companySettingsSchema>;

export const CLIENT_TYPES = ['PERSON', 'COMPANY', 'PUBLIC'] as const;

const clientFields = {
  type: z.enum(CLIENT_TYPES).optional(),
  contact: optionalText(160),
  phone: optionalText(40),
  email: optionalText(160),
  address: optionalText(300),
};

export const clientCreateSchema = z.object({ name: z.string().trim().min(2).max(200), ...clientFields });
export const clientUpdateSchema = z.object({ version: z.number().int().positive(), name: z.string().trim().min(2).max(200).optional(), ...clientFields });

/** Devis rapide (mode Simple) : un projet minimal, des lignes saisies, un devis — le tout d'un seul geste. */
export const quickQuoteSchema = z.object({
  title: z.string().trim().min(2).max(200),
  clientName: optionalText(200),
  location: optionalText(300),
  currency: z.enum(['XAF', 'XOF', 'EUR', 'USD']).optional(),
  vatEnabled: z.boolean().default(false),
  vatRate: rate.optional(),
  lines: z
    .array(
      z.object({
        designation: z.string().trim().min(1).max(500),
        unit: z.string().trim().min(1).max(20),
        quantity: positiveDecimal,
        unitPrice: positiveDecimal,
      }),
    )
    .min(1, { error: 'Ajoutez au moins une ligne' })
    .max(200),
});
export type QuickQuoteInput = z.input<typeof quickQuoteSchema>;
